import { DEFAULT_APP_ROUTE, ROOT_ROUTE_PATH } from './rootRoute';
import { AUTH_CALLBACK_GUARD, AUTH_GUARD } from '@/@core/services/auth/auth';
import { FeatureClient } from '@/@core/services/feature-flag/FeatureClient';
import { getEnabledFeatureRoutes } from '@/@core/utils/enabledFeatures';
import { accessibilityRoute } from '@/modules/Accessibility/route';
import { customizationRoute } from '@/modules/Customization/route';
import { easyLanguageRoute } from '@/modules/EasyLanguage/route';
import { homepageRoute } from '@/modules/Homepage/route';
import { signLanguageRoute } from '@/modules/SignLanguage/route';
import { redirectToChatRouteIfHomepageDisabled, withConfig } from '@/router/navigationGuards';
import { useUserStore } from '@/stores/userStore';
import { createRouter, createWebHistory, Router, RouteRecordRaw } from 'vue-router';

// Extend RouteMeta interface for TypeScript
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
  }
}

export async function initializeRouterWithFeatureFlags(
  featureClient: FeatureClient
): Promise<Router> {
  const rootRoute: RouteRecordRaw = {
    path: ROOT_ROUTE_PATH,
    name: 'App',
    component: () => import('@/@core/views/BaseView.vue'),
    children: [homepageRoute, accessibilityRoute, easyLanguageRoute, signLanguageRoute],
    beforeEnter: [withConfig],
    meta: {
      requiresAuth: true,
    },
  };
  const loginCallbackRoute = {
    path: '/callback',
    name: 'LoginCallback',
    beforeEnter: AUTH_CALLBACK_GUARD,
  } as RouteRecordRaw;

  const oauthCallbackRoute = {
    path: '/oauth/callback',
    name: 'OAuthCallback',
    component: () => import('@/@core/views/OAuthCallback.vue'),
    meta: {
      requiresAuth: false,
    },
  } as RouteRecordRaw;
  const notFoundRoute: RouteRecordRaw = {
    name: 'NotFound',
    path: '/:pathMatch(.*)*',
    component: () => import('@/@core/views/NotFound.vue'),
  };
  const routes: RouteRecordRaw[] = [
    rootRoute,
    loginCallbackRoute,
    oauthCallbackRoute,
    notFoundRoute,
  ];

  const enabledFeatureRoutes = getEnabledFeatureRoutes(featureClient);
  enabledFeatureRoutes.forEach((route) => rootRoute.children.push(route));

  // Redirect root to configured default app route so it becomes the default landing page
  rootRoute.children.unshift({ path: '', redirect: DEFAULT_APP_ROUTE, name: 'DefaultRoute' });

  // Apply homepage disabled redirect guard specifically to the homepage path
  const homepageChild = rootRoute.children.find((r) => r.name === 'Homepage');
  if (homepageChild) {
    const existingHomepageGuards = (homepageChild.beforeEnter as any[] | undefined) ?? [];
    homepageChild.beforeEnter = [
      ...existingHomepageGuards,
      redirectToChatRouteIfHomepageDisabled(!featureClient.homepageEnabled()),
    ];
  }

  const userStore = useUserStore();
  await userStore.fetch();
  if (featureClient.customizationViewEnabled() && userStore.isAdmin) {
    rootRoute.children.push(customizationRoute);
  }

  const router = createRouter({
    history: createWebHistory(),
    routes,
  });
  router.beforeEach(AUTH_GUARD);

  return router;
}
